import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addPayrollItem,
  closePayrollPeriod,
  createPayrollPeriod,
  createSupportDoc,
  deletePayrollItem,
  getPayrollPeriod,
  issueSupportDoc,
  listPayrollPeriods,
  listSupportDocs,
} from './payroll.api';
import { formatCop } from '../../lib/format';
import { readApiError } from '../../lib/api';

type Tab = 'payroll' | 'support-docs';

export function PayrollPage() {
  const [tab, setTab] = useState<Tab>('payroll');

  return (
    <section>
      <h1 style={{ marginTop: 0 }}>Nómina y Documentos Soporte</h1>
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem' }}>
        <button className={tab === 'payroll' ? 'btn btn-primary' : 'btn'} onClick={() => setTab('payroll')}>
          Nómina
        </button>
        <button
          className={tab === 'support-docs' ? 'btn btn-primary' : 'btn'}
          onClick={() => setTab('support-docs')}
        >
          Documentos Soporte
        </button>
      </div>

      {tab === 'payroll' && <PayrollTab />}
      {tab === 'support-docs' && <SupportDocsTab />}
    </section>
  );
}

function PayrollTab() {
  const qc = useQueryClient();
  const periods = useQuery({ queryKey: ['payroll', 'periods'], queryFn: listPayrollPeriods });
  const [selected, setSelected] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: createPayrollPeriod,
    onSuccess: (p) => {
      setShowCreate(false);
      setSelected(p.id);
      void qc.invalidateQueries({ queryKey: ['payroll', 'periods'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: '1rem' }}>
      <aside>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)} style={{ width: '100%', marginBottom: '0.75rem' }}>
          + Nuevo periodo
        </button>
        {error && <p className="error">{error}</p>}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {periods.data?.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p.id)}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                padding: '0.75rem 1rem',
                background: selected === p.id ? 'var(--color-bg)' : 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--color-border)',
                cursor: 'pointer',
              }}
            >
              <strong>{p.label}</strong>
              <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)' }}>
                {new Date(p.startDate).toLocaleDateString('es-CO')} —{' '}
                {new Date(p.endDate).toLocaleDateString('es-CO')}
              </div>
              <div style={{ fontSize: '0.8rem' }}>
                {p.closedAt ? (
                  <span style={{ color: 'var(--color-accent)' }}>Cerrado · {formatCop(p.totalNet)}</span>
                ) : (
                  <span style={{ color: 'var(--color-muted)' }}>{p._count?.items ?? 0} liquidaciones</span>
                )}
              </div>
            </button>
          ))}
          {periods.data?.length === 0 && (
            <p style={{ padding: '1rem', color: 'var(--color-muted)' }}>Sin periodos.</p>
          )}
        </div>
      </aside>

      <div>{selected ? <PeriodDetail periodId={selected} /> : <p style={{ color: 'var(--color-muted)' }}>Selecciona o crea un periodo.</p>}</div>

      {showCreate && (
        <CreatePeriodModal
          onCancel={() => setShowCreate(false)}
          onSubmit={(b) => createMut.mutate(b)}
          submitting={createMut.isPending}
        />
      )}
    </div>
  );
}

function PeriodDetail({ periodId }: { periodId: number }) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const period = useQuery({
    queryKey: ['payroll', 'period', periodId],
    queryFn: () => getPayrollPeriod(periodId),
  });

  const addMut = useMutation({
    mutationFn: (body: { userId: number; baseSalary: number; bonuses: number; deductions: number }) =>
      addPayrollItem(periodId, body),
    onSuccess: () => {
      setShowAdd(false);
      void qc.invalidateQueries({ queryKey: ['payroll', 'period', periodId] });
      void qc.invalidateQueries({ queryKey: ['payroll', 'periods'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const delMut = useMutation({
    mutationFn: (itemId: number) => deletePayrollItem(periodId, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payroll', 'period', periodId] }),
  });

  const closeMut = useMutation({
    mutationFn: () => closePayrollPeriod(periodId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['payroll'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  if (period.isLoading) return <p>Cargando...</p>;
  if (!period.data) return null;
  const p = period.data;
  const closed = !!p.closedAt;
  const total = p.items.reduce((s, x) => s + Number(x.net), 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0 }}>{p.label}</h2>
        {closed ? (
          <span className="btn" style={{ background: 'var(--color-accent)', borderColor: 'var(--color-accent)' }}>
            Cerrado
          </span>
        ) : (
          <>
            <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Liquidación</button>
            <button
              className="btn"
              disabled={p.items.length === 0 || closeMut.isPending}
              onClick={() => closeMut.mutate()}
            >
              Cerrar y posteear contable
            </button>
          </>
        )}
      </div>

      {error && <p className="error">{error}</p>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'left' }}>Empleado</th>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Base</th>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Bonos</th>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Deducciones</th>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Neto</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {p.items.map((it) => (
              <tr key={it.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem 1rem' }}>{it.user.fullName}</td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{formatCop(it.baseSalary)}</td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{formatCop(it.bonuses)}</td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>{formatCop(it.deductions)}</td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}><strong>{formatCop(it.net)}</strong></td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {!closed && (
                    <button
                      className="btn"
                      style={{ padding: '2px 8px', fontSize: '0.8rem' }}
                      onClick={() => delMut.mutate(it.id)}
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {p.items.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Sin liquidaciones.
                </td>
              </tr>
            )}
          </tbody>
          {p.items.length > 0 && (
            <tfoot>
              <tr style={{ background: 'var(--color-bg)' }}>
                <td colSpan={4} style={{ padding: '0.6rem 1rem', textAlign: 'right' }}><strong>Total nómina</strong></td>
                <td style={{ padding: '0.6rem 1rem', textAlign: 'right' }}><strong>{formatCop(total)}</strong></td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {showAdd && (
        <AddItemModal
          onCancel={() => setShowAdd(false)}
          onSubmit={(b) => addMut.mutate(b)}
          submitting={addMut.isPending}
        />
      )}
    </div>
  );
}

function CreatePeriodModal(props: {
  onCancel: () => void;
  onSubmit: (b: { label: string; startDate: string; endDate: string }) => void;
  submitting: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [label, setLabel] = useState('');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 400 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({ label, startDate, endDate });
        }}
      >
        <h3 style={{ marginTop: 0 }}>Nuevo periodo</h3>
        <div className="field">
          <label>Etiqueta</label>
          <input className="input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="2026-05 quincena 1" required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Inicio</label>
            <input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </div>
          <div className="field">
            <label>Fin</label>
            <input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Creando...' : 'Crear'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function AddItemModal(props: {
  onCancel: () => void;
  onSubmit: (b: { userId: number; baseSalary: number; bonuses: number; deductions: number }) => void;
  submitting: boolean;
}) {
  const [userId, setUserId] = useState('');
  const [baseSalary, setBaseSalary] = useState('0');
  const [bonuses, setBonuses] = useState('0');
  const [deductions, setDeductions] = useState('0');

  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 420 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({
            userId: Number(userId),
            baseSalary: Number(baseSalary),
            bonuses: Number(bonuses),
            deductions: Number(deductions),
          });
        }}
      >
        <h3 style={{ marginTop: 0 }}>Liquidación de empleado</h3>
        <div className="field">
          <label>ID de usuario</label>
          <input
            className="input"
            type="number"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="ID interno (tabla users)"
            required
          />
          <small style={{ color: 'var(--color-muted)' }}>
            Se valida contra usuarios provisionados localmente.
          </small>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Salario base</label>
            <input className="input" type="number" step="0.01" value={baseSalary} onChange={(e) => setBaseSalary(e.target.value)} />
          </div>
          <div className="field">
            <label>Bonos</label>
            <input className="input" type="number" step="0.01" value={bonuses} onChange={(e) => setBonuses(e.target.value)} />
          </div>
          <div className="field">
            <label>Deducciones</label>
            <input className="input" type="number" step="0.01" value={deductions} onChange={(e) => setDeductions(e.target.value)} />
          </div>
        </div>
        <p style={{ color: 'var(--color-muted)', fontSize: '0.85rem' }}>
          Neto: <strong>{formatCop(Number(baseSalary) + Number(bonuses) - Number(deductions))}</strong>
        </p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Guardando...' : 'Agregar'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

function SupportDocsTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['support-docs'], queryFn: listSupportDocs });
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: createSupportDoc,
    onSuccess: () => {
      setShowCreate(false);
      void qc.invalidateQueries({ queryKey: ['support-docs'] });
    },
    onError: (err) => setError(readApiError(err).message),
  });

  const issueMut = useMutation({
    mutationFn: issueSupportDoc,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['support-docs'] }),
    onError: (err) => setError(readApiError(err).message),
  });

  return (
    <div>
      <button className="btn btn-primary" onClick={() => setShowCreate(true)} style={{ marginBottom: '0.75rem' }}>
        + Documento soporte
      </button>
      {error && <p className="error">{error}</p>}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)' }}>
              <th style={{ padding: '0.5rem 1rem' }}>Número</th>
              <th style={{ padding: '0.5rem 1rem' }}>Proveedor</th>
              <th style={{ padding: '0.5rem 1rem' }}>NIT</th>
              <th style={{ padding: '0.5rem 1rem' }}>Total</th>
              <th style={{ padding: '0.5rem 1rem' }}>Fecha</th>
              <th style={{ padding: '0.5rem 1rem' }}>CUFE / Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data?.map((d) => (
              <tr key={d.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem 1rem' }}><code>{d.number}</code></td>
                <td style={{ padding: '0.5rem 1rem' }}>{d.supplierName}</td>
                <td style={{ padding: '0.5rem 1rem' }}>{d.supplierNit}</td>
                <td style={{ padding: '0.5rem 1rem' }}>{formatCop(d.total)}</td>
                <td style={{ padding: '0.5rem 1rem' }}>{new Date(d.issuedAt).toLocaleDateString('es-CO')}</td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {d.cufe ? (
                    <code style={{ fontSize: '0.75rem' }}>{d.cufe.slice(0, 16)}...</code>
                  ) : (
                    <em style={{ color: 'var(--color-muted)' }}>sin emitir</em>
                  )}
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {!d.cufe && (
                    <button className="btn" onClick={() => issueMut.mutate(d.id)}>
                      Emitir DIAN
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-muted)' }}>
                  Sin documentos soporte.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateSDModal
          onCancel={() => setShowCreate(false)}
          onSubmit={(b) => createMut.mutate(b)}
          submitting={createMut.isPending}
        />
      )}
    </div>
  );
}

function CreateSDModal(props: {
  onCancel: () => void;
  onSubmit: (b: { number: string; supplierName: string; supplierNit: string; total: number; issuedAt: string }) => void;
  submitting: boolean;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    number: '',
    supplierName: '',
    supplierNit: '',
    total: '0',
    issuedAt: today,
  });

  return (
    <div style={modalBackdrop}>
      <form
        className="card"
        style={{ width: 460 }}
        onSubmit={(e) => {
          e.preventDefault();
          props.onSubmit({
            number: form.number,
            supplierName: form.supplierName,
            supplierNit: form.supplierNit,
            total: Number(form.total),
            issuedAt: form.issuedAt,
          });
        }}
      >
        <h3 style={{ marginTop: 0 }}>Nuevo documento soporte</h3>
        <div className="field">
          <label>Número</label>
          <input className="input" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} required />
        </div>
        <div className="field">
          <label>Proveedor</label>
          <input className="input" value={form.supplierName} onChange={(e) => setForm({ ...form, supplierName: e.target.value })} required />
        </div>
        <div className="field">
          <label>NIT del proveedor</label>
          <input className="input" value={form.supplierNit} onChange={(e) => setForm({ ...form, supplierNit: e.target.value })} required />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
          <div className="field">
            <label>Total</label>
            <input className="input" type="number" step="0.01" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} required />
          </div>
          <div className="field">
            <label>Fecha</label>
            <input className="input" type="date" value={form.issuedAt} onChange={(e) => setForm({ ...form, issuedAt: e.target.value })} required />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" disabled={props.submitting}>
            {props.submitting ? 'Guardando...' : 'Crear'}
          </button>
          <button type="button" className="btn" onClick={props.onCancel}>Cancelar</button>
        </div>
      </form>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 100,
};
